import { useState, useCallback } from 'react';
import {
  Modal,
  Stepper,
  Group,
  Button,
  Text,
  Stack,
  Box,
  Card,
  Select,
  Table,
  Badge,
  SimpleGrid,
  ThemeIcon,
  ScrollArea,
  Accordion,
  Avatar,
  Alert,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  Upload,
  FileText,
  Check,
  X,
  AlertCircle,
  ChevronLeft,
  Users,
  Clock,
} from 'react-feather';
import { groupsApi, type GroupImportPreviewResponse, type GroupImportResult } from '../../../services/api/groups';
import classes from './UploadMatchedGroupsModal.module.css';

interface UploadMatchedGroupsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  onImportComplete: () => void;
  hasSessionDates?: boolean;
  hasTimeSlots?: boolean;
  onEditCohortDetails?: () => void;
}

type ImportStep = 0 | 1 | 2;

export function UploadMatchedGroupsModal({
  opened,
  onClose,
  cohortId,
  onImportComplete,
  hasSessionDates = true,
  hasTimeSlots = true,
  onEditCohortDetails,
}: UploadMatchedGroupsModalProps) {
  const [activeStep, setActiveStep] = useState<ImportStep>(0);
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [emailColumnIndex, setEmailColumnIndex] = useState<number | null>(null);
  const [groupColumnIndex, setGroupColumnIndex] = useState<number | null>(null);
  const [scheduledTimeSlotColumnIndex, setScheduledTimeSlotColumnIndex] = useState<number | null>(null);
  const [availableTimeSlotsColumnIndex, setAvailableTimeSlotsColumnIndex] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<GroupImportPreviewResponse | null>(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importResult, setImportResult] = useState<GroupImportResult | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setEmailColumnIndex(null);
    setGroupColumnIndex(null);
    setScheduledTimeSlotColumnIndex(null);
    setAvailableTimeSlotsColumnIndex(null);
    setPreviewData(null);
    setImportResult(null);
    onClose();
  };

  // Parse CSV file
  const parseCSV = useCallback((text: string) => {
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const parseRow = (row: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseRow(lines[0]);
    const data = lines.slice(1).map(parseRow);

    return { headers, data };
  }, []);

  // Auto-detect email, group, and time slot columns
  const autoDetectColumns = useCallback((headers: string[]) => {
    let emailIdx: number | null = null;
    let groupIdx: number | null = null;
    let scheduledTimeSlotIdx: number | null = null;
    let availableTimeSlotsIdx: number | null = null;

    headers.forEach((header, idx) => {
      const headerLower = header.toLowerCase().replace(/[_\s-]/g, '');

      // Detect email column
      if (headerLower.includes('email') || headerLower.includes('mail')) {
        emailIdx = idx;
      }

      // Detect group column
      if (headerLower.includes('group') || headerLower.includes('team') || headerLower.includes('pod')) {
        groupIdx = idx;
      }

      // Detect scheduled time slot column ("Best", "Scheduled Time Slot", etc.)
      if (
        headerLower === 'best' ||
        headerLower.includes('scheduledtimeslot') ||
        headerLower.includes('assignedtimeslot') ||
        headerLower.includes('timeslotassignment')
      ) {
        scheduledTimeSlotIdx = idx;
      }

      // Detect available time slots column ("All", "Available Time Slots", etc.)
      if (
        headerLower === 'all' ||
        headerLower.includes('availabletimeslots') ||
        headerLower.includes('availability') ||
        headerLower.includes('timeslotavailability')
      ) {
        availableTimeSlotsIdx = idx;
      }
    });

    return { emailIdx, groupIdx, scheduledTimeSlotIdx, availableTimeSlotsIdx };
  }, []);

  // Handle file drop
  const handleFileDrop = async (files: File[]) => {
    const selectedFile = files[0];
    if (!selectedFile) return;

    try {
      const text = await selectedFile.text();
      const { headers, data } = parseCSV(text);

      setFile(selectedFile);
      setCsvHeaders(headers);
      setCsvData(data);

      // Auto-detect columns
      const { emailIdx, groupIdx, scheduledTimeSlotIdx, availableTimeSlotsIdx } =
        autoDetectColumns(headers);
      setEmailColumnIndex(emailIdx);
      setGroupColumnIndex(groupIdx);
      setScheduledTimeSlotColumnIndex(scheduledTimeSlotIdx);
      setAvailableTimeSlotsColumnIndex(availableTimeSlotsIdx);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to parse CSV file',
        color: 'red',
      });
    }
  };

  // Generate preview via API
  const generatePreview = useCallback(async () => {
    if (emailColumnIndex === null || groupColumnIndex === null) {
      notifications.show({
        title: 'Error',
        message: 'Please select both Email and Group columns',
        color: 'red',
      });
      return;
    }

    setPreviewing(true);
    try {
      const response = await groupsApi.previewImport(cohortId, {
        csvData,
        emailColumnIndex,
        groupColumnIndex,
        scheduledTimeSlotColumnIndex: scheduledTimeSlotColumnIndex ?? undefined,
        availableTimeSlotsColumnIndex: availableTimeSlotsColumnIndex ?? undefined,
      });

      setPreviewData(response);
      setActiveStep(1);
    } catch (error) {
      notifications.show({
        title: 'Preview Error',
        message: error instanceof Error ? error.message : 'Failed to generate preview',
        color: 'red',
      });
    } finally {
      setPreviewing(false);
    }
  }, [
    cohortId,
    csvData,
    emailColumnIndex,
    groupColumnIndex,
    scheduledTimeSlotColumnIndex,
    availableTimeSlotsColumnIndex,
  ]);

  // Handle import via API
  const handleImport = async () => {
    if (!previewData) return;

    setImporting(true);

    try {
      // Build the groups with only matched participants, including time slot data
      const groupsToCreate = previewData.groups.map((group) => ({
        name: group.name,
        memberEmails: group.participants.filter((p) => p.matched).map((p) => p.email),
        scheduledTimeSlot: group.scheduledTimeSlot,
        availableTimeSlots: group.availableTimeSlots,
      }));

      const result = await groupsApi.executeImport(cohortId, {
        groups: groupsToCreate,
      });

      setImportResult(result);
      setActiveStep(2);

      notifications.show({
        title: 'Success',
        message: `Successfully created ${result.groupsCreated} groups with ${result.participantsAssigned} participants`,
        color: 'green',
      });

      // Notify parent to refresh
      onImportComplete();
    } catch (error) {
      notifications.show({
        title: 'Import Error',
        message: error instanceof Error ? error.message : 'Failed to import groups',
        color: 'red',
      });
    } finally {
      setImporting(false);
    }
  };

  // Check if we can proceed to next step
  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return file !== null && emailColumnIndex !== null && groupColumnIndex !== null;
      case 1:
        return previewData !== null && previewData.groups.length > 0;
      default:
        return true;
    }
  };

  // Get initials for avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // MED-363: Prerequisites blocking
  const missingSessionDates = !hasSessionDates;
  const missingTimeSlots = !hasTimeSlots;
  const isBlocked = missingSessionDates || missingTimeSlots;

  const getBlockingMessage = () => {
    if (missingSessionDates && missingTimeSlots) {
      return 'Session dates and time slots are not configured for this cohort. Configure these before uploading groups.';
    }
    if (missingSessionDates) {
      return 'Session dates are not configured for this cohort. Add session dates before uploading groups.';
    }
    if (missingTimeSlots) {
      return 'Time slots are not configured for this cohort. Add time slots before uploading groups.';
    }
    return '';
  };

  const handleEditCohortDetails = () => {
    handleClose();
    onEditCohortDetails?.();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Upload Matched Groups"
      size="lg"
      centered
    >
      <Stack gap="lg">
        {/* Stepper */}
        <Stepper active={activeStep} size="sm">
          <Stepper.Step label="Upload" />
          <Stepper.Step label="Preview" />
          <Stepper.Step label="Complete" />
        </Stepper>

        {/* MED-363: Prerequisites warning callout */}
        {isBlocked && activeStep === 0 && (
          <Alert
            icon={<AlertCircle size={18} />}
            color="yellow"
            variant="light"
            title={null}
          >
            <Stack gap="sm">
              <Text size="sm">{getBlockingMessage()}</Text>
              <Box>
                <Button
                  variant="light"
                  color="yellow"
                  size="xs"
                  onClick={handleEditCohortDetails}
                >
                  Edit Cohort Details
                </Button>
              </Box>
            </Stack>
          </Alert>
        )}

        {/* Step 1: Upload */}
        {activeStep === 0 && (
          <Stack gap="md">
            <Dropzone
              onDrop={handleFileDrop}
              accept={{ 'text/csv': ['.csv'] }}
              maxFiles={1}
              disabled={isBlocked}
              className={file ? classes.dropzoneWithFile : classes.dropzone}
              style={isBlocked ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
            >
              {file ? (
                <Stack align="center" gap="xs" py="xl">
                  <ThemeIcon size={60} radius={60} variant="light" color="medleyGreen">
                    <FileText size={30} />
                  </ThemeIcon>
                  <Text fw={500}>{file.name}</Text>
                  <Text size="sm" c="dimmed">
                    {csvData.length} rows detected
                  </Text>
                  <Text size="sm" c="medleyGreen" style={{ cursor: 'pointer' }}>
                    Choose different file
                  </Text>
                </Stack>
              ) : (
                <Stack align="center" gap="xs" py="xl">
                  <Upload size={32} color="gray" />
                  <Text fw={500}>Drag and drop your CSV file here</Text>
                  <Text size="sm" c="dimmed">
                    or click to browse
                  </Text>
                </Stack>
              )}
            </Dropzone>

            {/* Column Selection */}
            {file && csvHeaders.length > 0 && (
              <Card withBorder p="md">
                <Stack gap="md">
                  <Text size="sm" fw={500}>
                    Select the columns for matching
                  </Text>
                  <Group grow>
                    <Select
                      label="Email Column"
                      placeholder="Select email column"
                      data={csvHeaders.map((h, i) => ({ value: String(i), label: h }))}
                      value={emailColumnIndex !== null ? String(emailColumnIndex) : null}
                      onChange={(value) =>
                        setEmailColumnIndex(value !== null ? parseInt(value, 10) : null)
                      }
                      leftSection={
                        emailColumnIndex !== null ? (
                          <ThemeIcon color="medleyGreen" variant="light" size="xs">
                            <Check size={12} />
                          </ThemeIcon>
                        ) : undefined
                      }
                    />
                    <Select
                      label="Group Column"
                      placeholder="Select group column"
                      data={csvHeaders.map((h, i) => ({ value: String(i), label: h }))}
                      value={groupColumnIndex !== null ? String(groupColumnIndex) : null}
                      onChange={(value) =>
                        setGroupColumnIndex(value !== null ? parseInt(value, 10) : null)
                      }
                      leftSection={
                        groupColumnIndex !== null ? (
                          <ThemeIcon color="medleyGreen" variant="light" size="xs">
                            <Check size={12} />
                          </ThemeIcon>
                        ) : undefined
                      }
                    />
                  </Group>

                  {/* Time Slot Columns (Optional) */}
                  <Text size="xs" fw={500} c="dimmed" mt="xs">
                    Time Slot Columns (Optional)
                  </Text>
                  <Group grow>
                    <Select
                      label="Scheduled Time Slot"
                      placeholder="Select column (optional)"
                      data={[
                        { value: '', label: 'None' },
                        ...csvHeaders.map((h, i) => ({ value: String(i), label: h })),
                      ]}
                      value={
                        scheduledTimeSlotColumnIndex !== null
                          ? String(scheduledTimeSlotColumnIndex)
                          : ''
                      }
                      onChange={(value) =>
                        setScheduledTimeSlotColumnIndex(value ? parseInt(value, 10) : null)
                      }
                      leftSection={
                        scheduledTimeSlotColumnIndex !== null ? (
                          <ThemeIcon color="medleyGreen" variant="light" size="xs">
                            <Clock size={12} />
                          </ThemeIcon>
                        ) : undefined
                      }
                      clearable
                    />
                    <Select
                      label="Available Time Slots"
                      placeholder="Select column (optional)"
                      data={[
                        { value: '', label: 'None' },
                        ...csvHeaders.map((h, i) => ({ value: String(i), label: h })),
                      ]}
                      value={
                        availableTimeSlotsColumnIndex !== null
                          ? String(availableTimeSlotsColumnIndex)
                          : ''
                      }
                      onChange={(value) =>
                        setAvailableTimeSlotsColumnIndex(value ? parseInt(value, 10) : null)
                      }
                      leftSection={
                        availableTimeSlotsColumnIndex !== null ? (
                          <ThemeIcon color="medleyGreen" variant="light" size="xs">
                            <Clock size={12} />
                          </ThemeIcon>
                        ) : undefined
                      }
                      clearable
                    />
                  </Group>

                  {emailColumnIndex !== null && groupColumnIndex !== null && (
                    <Text size="xs" c="dimmed">
                      Columns auto-detected. Verify they are correct before continuing.
                    </Text>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        )}

        {/* Step 2: Preview */}
        {activeStep === 1 && previewData && (
          <Stack gap="md">
            {/* Summary Stats */}
            <SimpleGrid cols={4}>
              <Card withBorder p="sm" className={classes.statCard}>
                <Text size="xl" fw={700}>
                  {previewData.summary.totalGroups}
                </Text>
                <Text size="xs" c="dimmed">
                  Groups
                </Text>
              </Card>
              <Card
                withBorder
                p="sm"
                className={classes.statCard}
                style={{ borderLeftColor: 'var(--mantine-color-green-6)' }}
              >
                <Text size="xl" fw={700} c="green">
                  {previewData.summary.totalMatched}
                </Text>
                <Text size="xs" c="dimmed">
                  Matched
                </Text>
              </Card>
              <Card
                withBorder
                p="sm"
                className={classes.statCard}
                style={{ borderLeftColor: 'var(--mantine-color-red-6)' }}
              >
                <Text size="xl" fw={700} c="red">
                  {previewData.summary.totalUnmatched}
                </Text>
                <Text size="xs" c="dimmed">
                  Not Found
                </Text>
              </Card>
              <Card
                withBorder
                p="sm"
                className={classes.statCard}
                style={{ borderLeftColor: 'var(--mantine-color-yellow-6)' }}
              >
                <Text size="xl" fw={700} c="yellow">
                  {previewData.summary.totalAlreadyAssigned}
                </Text>
                <Text size="xs" c="dimmed">
                  Reassigned
                </Text>
              </Card>
            </SimpleGrid>

            {/* Time Slot Info */}
            {previewData.timeSlotInfo?.hasTimeSlotData && (
              <Card withBorder p="sm">
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="medleyGreen">
                    <Clock size={14} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    Time Slot Data Detected
                  </Text>
                </Group>
                <Group gap="md">
                  <Text size="xs" c="dimmed">
                    Valid:{' '}
                    <Text component="span" c="green" fw={500}>
                      {previewData.timeSlotInfo.validTimeSlots.length} time slots
                    </Text>
                  </Text>
                  {previewData.timeSlotInfo.invalidTimeSlots.length > 0 && (
                    <Text size="xs" c="dimmed">
                      Invalid:{' '}
                      <Text component="span" c="red" fw={500}>
                        {previewData.timeSlotInfo.invalidTimeSlots.join(', ')}
                      </Text>
                    </Text>
                  )}
                </Group>
              </Card>
            )}

            {/* MED-1290: explain why rows have no group. Before this, a file whose
                group column couldn't be parsed produced an empty preview and a
                disabled "Create 0 Groups" button with nothing said about the
                cause — one CM gave up and hand-assigned 249 participants. */}
            {previewData.groupColumnInfo && (
              <Alert
                variant="light"
                color={previewData.groups.length === 0 ? 'red' : 'yellow'}
                icon={<AlertCircle size={16} />}
                title={
                  previewData.groups.length === 0
                    ? 'No groups could be created'
                    : 'Some rows had no usable group number'
                }
              >
                <Stack gap={4}>
                  {previewData.groupColumnInfo.unreadableCount > 0 && (
                    <Text size="xs">
                      {previewData.groupColumnInfo.unreadableCount}{' '}
                      {previewData.groupColumnInfo.unreadableCount === 1 ? 'row' : 'rows'} had a value in{' '}
                      <Text component="span" fw={600}>
                        {groupColumnIndex !== null ? csvHeaders[groupColumnIndex] : 'the group column'}
                      </Text>{' '}
                      that isn&apos;t a whole number:{' '}
                      <Text component="span" fw={600}>
                        {previewData.groupColumnInfo.unreadableValues.slice(0, 5).join(', ')}
                      </Text>
                      {previewData.groupColumnInfo.unreadableValues.length > 5 &&
                        ` and ${previewData.groupColumnInfo.unreadableValues.length - 5} more`}
                      .
                    </Text>
                  )}
                  {previewData.groupColumnInfo.emptyCount > 0 && (
                    <Text size="xs">
                      {previewData.groupColumnInfo.emptyCount}{' '}
                      {previewData.groupColumnInfo.emptyCount === 1 ? 'row' : 'rows'} had no value in{' '}
                      <Text component="span" fw={600}>
                        {groupColumnIndex !== null ? csvHeaders[groupColumnIndex] : 'the group column'}
                      </Text>
                      .
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    {previewData.groupColumnInfo.unreadableCount > 0 &&
                      'The group column must hold whole numbers — 1, 2, 3. '}
                    Go Back to map a different column, or fix the values in the CSV and re-upload.
                    {previewData.groups.length > 0 &&
                      ' You can also continue and assign these participants to groups by hand.'}
                  </Text>
                </Stack>
              </Alert>
            )}

            {/* Groups Preview */}
            <ScrollArea h={350}>
              <Accordion multiple defaultValue={previewData.groups.map((g) => String(g.groupNumber))}>
                {previewData.groups.map((group) => {
                  const matchedCount = group.participants.filter((p) => p.matched).length;
                  const unmatchedCount = group.participants.filter((p) => !p.matched).length;

                  return (
                    <Accordion.Item key={group.groupNumber} value={String(group.groupNumber)}>
                      <Accordion.Control>
                        <Group justify="space-between" pr="md">
                          <Group gap="sm">
                            <ThemeIcon size="md" radius="md" color="medleyGreen" variant="light">
                              <Users size={14} />
                            </ThemeIcon>
                            <Text fw={500}>{group.name}</Text>
                            {group.scheduledTimeSlot && (
                              <Badge color="gray" variant="light" size="sm" leftSection={<Clock size={10} />}>
                                {group.scheduledTimeSlot}
                              </Badge>
                            )}
                          </Group>
                          <Group gap="xs">
                            <Badge color="medleyGreen" variant="light" size="sm">
                              {matchedCount} matched
                            </Badge>
                            {unmatchedCount > 0 && (
                              <Badge color="red" variant="light" size="sm">
                                {unmatchedCount} not found
                              </Badge>
                            )}
                          </Group>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Table striped>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Participant</Table.Th>
                              <Table.Th>Email</Table.Th>
                              <Table.Th>Status</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {group.participants.map((participant, idx) => (
                              <Table.Tr key={idx}>
                                <Table.Td>
                                  {participant.matched ? (
                                    <Group gap="xs">
                                      <Avatar size="sm" radius="xl" color="medleyGreen">
                                        {getInitials(participant.firstName, participant.lastName)}
                                      </Avatar>
                                      <Text size="sm">
                                        {participant.firstName} {participant.lastName}
                                      </Text>
                                    </Group>
                                  ) : (
                                    <Text size="sm" c="dimmed">Unknown</Text>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" c={participant.matched ? undefined : 'dimmed'}>
                                    {participant.email}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  {participant.matched ? (
                                    participant.alreadyAssigned ? (
                                      <Group gap="xs">
                                        <ThemeIcon color="yellow" variant="light" size="xs">
                                          <AlertCircle size={10} />
                                        </ThemeIcon>
                                        <Text size="xs" c="yellow">
                                          From: {participant.currentGroupName || 'another group'}
                                        </Text>
                                      </Group>
                                    ) : (
                                      <ThemeIcon color="medleyGreen" variant="light" size="xs">
                                        <Check size={10} />
                                      </ThemeIcon>
                                    )
                                  ) : (
                                    <Group gap="xs">
                                      <ThemeIcon color="red" variant="light" size="xs">
                                        <X size={10} />
                                      </ThemeIcon>
                                      <Text size="xs" c="red">
                                        Not in cohort
                                      </Text>
                                    </Group>
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>

              {/* Unassigned Section */}
              {previewData.unassigned.length > 0 && (
                <Card withBorder mt="md" className={classes.unassignedCard}>
                  <Text size="sm" fw={500} mb="sm" c="dimmed">
                    Rows without group assignment ({previewData.unassigned.length})
                  </Text>
                  <Stack gap="xs">
                    {previewData.unassigned.slice(0, 5).map((participant, idx) => (
                      <Group key={idx} gap="xs">
                        <Text size="sm">{participant.email}</Text>
                        {!participant.matched && (
                          <Badge size="xs" color="red" variant="light">
                            Not in cohort
                          </Badge>
                        )}
                      </Group>
                    ))}
                    {previewData.unassigned.length > 5 && (
                      <Text size="xs" c="dimmed">
                        ... and {previewData.unassigned.length - 5} more
                      </Text>
                    )}
                  </Stack>
                </Card>
              )}
            </ScrollArea>
          </Stack>
        )}

        {/* Step 3: Complete */}
        {activeStep === 2 && importResult && (
          <Stack align="center" gap="md" py="xl">
            <ThemeIcon size={80} radius={80} color="medleyGreen" variant="light">
              <Check size={40} />
            </ThemeIcon>
            <Text size="xl" fw={600}>
              Groups Created!
            </Text>
            <Text c="dimmed" ta="center">
              Successfully created {importResult.groupsCreated} groups with {importResult.participantsAssigned} participants assigned.
            </Text>

            {/* Created Groups Summary */}
            <Card withBorder p="md" w="100%">
              <Stack gap="xs">
                {importResult.groups.map((group) => (
                  <Group key={group.id} justify="space-between">
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="sm" color="medleyGreen" variant="light">
                        <Users size={12} />
                      </ThemeIcon>
                      <Text size="sm">{group.name}</Text>
                    </Group>
                    <Badge color="medleyGreen" variant="light" size="sm">
                      {group.memberCount} members
                    </Badge>
                  </Group>
                ))}
              </Stack>
            </Card>

            {/* Unassigned Warning */}
            {importResult.unassignedEmails.length > 0 && (
              <Card withBorder p="md" w="100%" className={classes.warningCard}>
                <Group gap="xs" mb="sm">
                  <ThemeIcon color="yellow" variant="light" size="sm">
                    <AlertCircle size={14} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    {importResult.unassignedEmails.length} emails could not be assigned
                  </Text>
                </Group>
                <Text size="xs" c="dimmed">
                  These emails were not found in the cohort: {importResult.unassignedEmails.slice(0, 3).join(', ')}
                  {importResult.unassignedEmails.length > 3 && ` and ${importResult.unassignedEmails.length - 3} more`}
                </Text>
              </Card>
            )}
          </Stack>
        )}

        {/* Footer */}
        <Group justify="space-between" className="modal-footer">
          {activeStep > 0 && activeStep < 2 ? (
            <Button
              variant="subtle"
              leftSection={<ChevronLeft size={16} />}
              onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0) as ImportStep)}
            >
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Group gap="sm">
            <Button variant="default" onClick={handleClose}>
              {activeStep === 2 ? 'Done' : 'Cancel'}
            </Button>
            {activeStep === 0 && (
              <Button
                onClick={generatePreview}
                disabled={isBlocked || !canProceed()}
                loading={previewing}
              >
                Continue
              </Button>
            )}
            {activeStep === 1 && (
              <Button
                onClick={handleImport}
                disabled={!canProceed()}
                loading={importing}
              >
                Create {previewData?.groups.length || 0} Groups
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
